import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Badge,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Fade,
  Skeleton,
  Snackbar,
  Alert,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  LocalAtm as AtmIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  TrendingDown as DownIcon,
  Assessment as ReportIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// ... rest of the code remains the same

const ROLES = [
  { value: 'admin', label: 'Admin', color: '#d32f2f', icon: '👨‍💼' },
  { value: 'manager', label: 'Manager', color: '#7b1fa2', icon: '👔' },
  { value: 'chef', label: 'Chef', color: '#f57c00', icon: '👨‍🍳' },
  { value: 'waiter', label: 'Waiter', color: '#1976d2', icon: '🍽️' },
  { value: 'cashier', label: 'Cashier', color: '#388e3c', icon: '💵' },
  { value: 'cleaner', label: 'Cleaner', color: '#5d4037', icon: '🧹' },
  { value: 'guard', label: 'Security', color: '#455a64', icon: '👮' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const StaffManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialogs
  const [staffDialog, setStaffDialog] = useState(false);
  const [staffForm, setStaffForm] = useState({
    name: '', role: 'waiter', phone: '', email: '', password: '',
    pin_code: '', base_salary: '', joining_date: new Date().toISOString().split('T')[0],
    address: '', emergency_contact: '', cnic: '', bank_account: '',
    is_active: true,
  });

  const [attendanceDialog, setAttendanceDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    check_in: '09:00', check_out: '18:00', status: 'present',
    overtime: 0, notes: '',
  });

  const [salaryDialog, setSalaryDialog] = useState(false);
  const [salaryForm, setSalaryForm] = useState({
    month: new Date().getMonth() + 1, year: new Date().getFullYear(),
    base_salary: '', bonus: 0, deductions: 0, advance_deducted: 0,
    loan_deducted: 0, overtime_pay: 0, net_salary: '',
  });

  const [loanDialog, setLoanDialog] = useState(false);
  const [loanForm, setLoanForm] = useState({
    type: 'advance', amount: '', date: new Date().toISOString().split('T')[0],
    reason: '', installments: 1, monthly_deduction: '',
  });

  const [detailDialog, setDetailDialog] = useState(false);
  const [detailStaff, setDetailStaff] = useState(null);

  // ==================== HELPERS ====================
  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const formatCurrency = useCallback((amount) => {
    const val = Number(amount || 0);
    return `PKR ${val.toLocaleString('en-IN')}`;
  }, []);

  const getRoleInfo = useCallback((role) => {
    return ROLES.find((r) => r.value === role) || ROLES[3];
  }, []);

  // ==================== SAFE API ACCESS ====================
  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI) {
      return window.electronAPI;
    }
    return null;
  }, []);

  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[Staff] Storage not available:', e);
      return null;
    }
  }, []);

  // ==================== LOAD DATA ====================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = getAPI();
      let staffData, attData, salData, loanData;

      if (!api) {
        console.log('[Staff] Browser mode - using storage');
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');

        [staffData, attData, salData, loanData] = await Promise.all([
          storage.getStaff ? storage.getStaff() : Promise.resolve([]),
          storage.getAttendance ? storage.getAttendance() : Promise.resolve([]),
          storage.getSalaryRecords ? storage.getSalaryRecords() : Promise.resolve([]),
          storage.getLoans ? storage.getLoans() : Promise.resolve([]),
        ]);
      } else {
        console.log('[Staff] Electron mode - using electronAPI');
        [staffData, attData, salData, loanData] = await Promise.all([
          api.getStaff(),
          api.getAttendance ? api.getAttendance() : Promise.resolve([]),
          api.getSalaryRecords ? api.getSalaryRecords() : Promise.resolve([]),
          api.getLoans ? api.getLoans() : Promise.resolve([]),
        ]);
      }

      setStaff(staffData || []);
      setAttendance(attData || []);
      setSalaryRecords(salData || []);
      setLoans(loanData || []);
    } catch (err) {
      console.error('[Staff] Load error:', err);
      showMessage('❌ Data load nahi ho saki!', 'error');
    } finally {
      setLoading(false);
    }
  }, [getAPI, getStorage, showMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==================== HELPERS ====================
  const filteredStaff = staff.filter((s) =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.phone?.includes(searchQuery) ||
    s.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStaffAttendance = useCallback((staffId, month, year) => {
    return attendance.filter((a) => {
      try {
        const d = new Date(a.date);
        return a.staff_id === staffId && d.getMonth() + 1 === month && d.getFullYear() === year;
      } catch {
        return false;
      }
    });
  }, [attendance]);

  const getStaffLoans = useCallback((staffId) => {
    return loans.filter((l) => l.staff_id === staffId && l.status === 'active');
  }, [loans]);

  const getStaffSalary = useCallback((staffId, month, year) => {
    return salaryRecords.find((s) => s.staff_id === staffId && s.month === month && s.year === year);
  }, [salaryRecords]);

  // ==================== CRUD OPERATIONS ====================
  const saveStaff = async () => {
    if (!staffForm.name.trim() || !staffForm.phone) {
      showMessage('❌ Name and Phone required!', 'error');
      return;
    }
    try {
      const api = getAPI();
      const payload = {
        ...staffForm,
        base_salary: parseFloat(staffForm.base_salary) || 0,
        is_active: staffForm.is_active ? 1 : 0,
      };

      if (!api) {
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        
        if (selectedStaff) {
          await storage.updateStaff(selectedStaff.id, payload);
        } else {
          await storage.addStaff(payload);
        }
      } else {
        if (selectedStaff) {
          await api.updateStaff(selectedStaff.id, payload);
        } else {
          await api.addStaff(payload);
        }
      }
      
      setStaffDialog(false);
      setSelectedStaff(null);
      showMessage(`✅ ${selectedStaff ? 'Updated' : 'Added'} ${staffForm.name}!`, 'success');
      loadData();
    } catch (err) {
      console.error('[Staff] Save error:', err);
      showMessage(`❌ Save nahi ho saka: ${err.message}`, 'error');
    }
  };

  const saveAttendance = async () => {
    if (!selectedStaff) return;
    try {
      const api = getAPI();
      const payload = {
        ...attendanceForm,
        staff_id: selectedStaff.id,
        overtime: parseFloat(attendanceForm.overtime) || 0,
      };

      if (!api) {
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        await storage.addAttendance(payload);
      } else {
        await api.addAttendance(payload);
      }
      
      setAttendanceDialog(false);
      showMessage('✅ Attendance marked!', 'success');
      loadData();
    } catch (err) {
      console.error('[Staff] Attendance error:', err);
      showMessage(`❌ Attendance save nahi ho saki: ${err.message}`, 'error');
    }
  };

  const saveSalary = async () => {
    if (!selectedStaff) return;
    try {
      const net = parseFloat(salaryForm.base_salary) + parseFloat(salaryForm.bonus || 0) +
        parseFloat(salaryForm.overtime_pay || 0) - parseFloat(salaryForm.deductions || 0) -
        parseFloat(salaryForm.advance_deducted || 0) - parseFloat(salaryForm.loan_deducted || 0);
      
      const api = getAPI();
      const payload = {
        ...salaryForm,
        staff_id: selectedStaff.id,
        net_salary: net,
      };

      if (!api) {
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        await storage.addSalaryRecord(payload);
      } else {
        await api.addSalaryRecord(payload);
      }
      
      setSalaryDialog(false);
      showMessage('✅ Salary processed!', 'success');
      loadData();
    } catch (err) {
      console.error('[Staff] Salary error:', err);
      showMessage(`❌ Salary save nahi ho saki: ${err.message}`, 'error');
    }
  };

  const saveLoan = async () => {
    if (!selectedStaff) return;
    try {
      const api = getAPI();
      const payload = {
        ...loanForm,
        staff_id: selectedStaff.id,
        amount: parseFloat(loanForm.amount) || 0,
        monthly_deduction: parseFloat(loanForm.monthly_deduction) || 0,
        installments: parseInt(loanForm.installments) || 1,
      };

      if (!api) {
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        await storage.addLoan(payload);
      } else {
        await api.addLoan(payload);
      }
      
      setLoanDialog(false);
      showMessage('✅ Loan added!', 'success');
      loadData();
    } catch (err) {
      console.error('[Staff] Loan error:', err);
      showMessage(`❌ Loan save nahi ho saka: ${err.message}`, 'error');
    }
  };

  const openStaffDialog = (editStaff = null) => {
    if (editStaff) {
      setSelectedStaff(editStaff);
      setStaffForm({ ...editStaff, is_active: editStaff.is_active === 1 });
    } else {
      setSelectedStaff(null);
      setStaffForm({
        name: '', role: 'waiter', phone: '', email: '', password: '',
        pin_code: '', base_salary: '', joining_date: new Date().toISOString().split('T')[0],
        address: '', emergency_contact: '', cnic: '', bank_account: '',
        is_active: true,
      });
    }
    setStaffDialog(true);
  };

  const openAttendanceDialog = (staff) => {
    setSelectedStaff(staff);
    setAttendanceForm({
      date: new Date().toISOString().split('T')[0],
      check_in: '09:00', check_out: '18:00', status: 'present',
      overtime: 0, notes: '',
    });
    setAttendanceDialog(true);
  };

  const openSalaryDialog = (staff) => {
    setSelectedStaff(staff);
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const existing = getStaffSalary(staff.id, currentMonth, currentYear);
    
    setSalaryForm(existing || {
      month: currentMonth, year: currentYear,
      base_salary: staff.base_salary || 0, bonus: 0, deductions: 0,
      advance_deducted: 0, loan_deducted: 0, overtime_pay: 0, net_salary: staff.base_salary || 0,
    });
    setSalaryDialog(true);
  };

  const openLoanDialog = (staff) => {
    setSelectedStaff(staff);
    setLoanForm({
      type: 'advance', amount: '', date: new Date().toISOString().split('T')[0],
      reason: '', installments: 1, monthly_deduction: '',
    });
    setLoanDialog(true);
  };

  const openDetailDialog = (staff) => {
    setDetailStaff(staff);
    setDetailDialog(true);
  };

  const deleteStaff = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      const api = getAPI();
      if (!api) {
        const storage = await getStorage();
        if (storage) await storage.deleteStaff(id);
      } else {
        await api.deleteStaff(id);
      }
      showMessage('✅ Staff deleted!', 'success');
      loadData();
    } catch (err) {
      console.error('[Staff] Delete error:', err);
      showMessage(`❌ Delete nahi ho saka: ${err.message}`, 'error');
    }
  };

  // ==================== STAT CARD ====================
  const StatCard = ({ label, value, color, icon }) => (
    <Grid item xs={6} md={3}>
      <Fade in timeout={300}>
        <Card sx={{ 
          borderRadius: 3, 
          borderLeft: `4px solid ${color}`,
          transition: 'all 0.2s',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
        }}>
          <CardContent sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1.5, sm: 2 }
          }}>
            <Avatar sx={{ bgcolor: `${color}15`, color: color, width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 } }}>
              {icon}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: color, 
                fontSize: { xs: '1.1rem', sm: '1.3rem' } 
              }}>
                {value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                {label}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );

  // ==================== RENDER STAFF LIST ====================
  const renderStaffList = () => (
    <Box>
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: 2 
      }}>
        <TextField
          size="small"
          placeholder="Search staff..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: '100%', sm: 300 } }}
          InputProps={{ 
            startAdornment: <SearchIcon fontSize="small" color="action" /> 
          }}
        />
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => openStaffDialog()} 
          fullWidth={isMobile}
          sx={{ 
            bgcolor: '#ff6b35', 
            '&:hover': { bgcolor: '#e55a2b' },
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          Add Staff
        </Button>
      </Box>

      <Grid container spacing={2}>
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
            </Grid>
          ))
        ) : filteredStaff.length > 0 ? (
          filteredStaff.map((member) => {
            const roleInfo = getRoleInfo(member.role);
            const activeLoans = getStaffLoans(member.id);
            const loanAmount = activeLoans.reduce((sum, l) => sum + (l.remaining || 0), 0);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
                <Fade in timeout={300}>
                  <Card sx={{ 
                    borderRadius: 3, 
                    borderLeft: `4px solid ${roleInfo.color}`, 
                    transition: 'all 0.2s', 
                    height: '100%',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } 
                  }}>
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Avatar sx={{ bgcolor: roleInfo.color, width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                          {roleInfo.icon}
                        </Avatar>
                        <Box>
                          <Chip 
                            size="small" 
                            label={member.is_active ? 'Active' : 'Inactive'} 
                            color={member.is_active ? 'success' : 'default'} 
                            sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 20 }} 
                          />
                          {loanAmount > 0 && (
                            <Tooltip title={`Pending Loan: ${formatCurrency(loanAmount)}`}>
                              <Chip 
                                size="small" 
                                icon={<WarningIcon fontSize="small" />} 
                                label="Loan" 
                                color="warning" 
                                sx={{ ml: 0.5, fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 20 }} 
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        color: '#1a1a2e', 
                        fontSize: { xs: '0.95rem', sm: '1.1rem' } 
                      }}>
                        {member.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: roleInfo.color, fontWeight: 600, mb: 1, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                        {roleInfo.label}
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          <PersonIcon fontSize="small" /> {member.phone}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          <MoneyIcon fontSize="small" /> Salary: {formatCurrency(member.base_salary)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          <CalendarIcon fontSize="small" /> Joined: {member.joining_date}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          startIcon={<ViewIcon />} 
                          onClick={() => openDetailDialog(member)} 
                          fullWidth
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        >
                          View
                        </Button>
                        <IconButton 
                          size="small" 
                          onClick={() => openStaffDialog(member)} 
                          sx={{ color: '#1976d2', p: { xs: 0.5, sm: 0.8 } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => deleteStaff(member.id)} 
                          sx={{ color: '#f44336', p: { xs: 0.5, sm: 0.8 } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button 
                          size="small" 
                          variant="text" 
                          startIcon={<TimeIcon />} 
                          onClick={() => openAttendanceDialog(member)} 
                          sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                        >
                          {isMobile ? '' : 'Attendance'}
                        </Button>
                        <Button 
                          size="small" 
                          variant="text" 
                          startIcon={<MoneyIcon />} 
                          onClick={() => openSalaryDialog(member)} 
                          sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                        >
                          {isMobile ? '' : 'Salary'}
                        </Button>
                        <Button 
                          size="small" 
                          variant="text" 
                          startIcon={<AtmIcon />} 
                          onClick={() => openLoanDialog(member)} 
                          sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                        >
                          {isMobile ? '' : 'Loan'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <PersonIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6">No staff found</Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>کوئی سٹاف نہیں ملا</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  // ==================== RENDER ATTENDANCE ====================
  const renderAttendance = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayAttendance = attendance.filter((a) => a.date === today);

    return (
      <Box>
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: 1.5 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
            Attendance / حاضری - {today}
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />} 
            onClick={loadData} 
            fullWidth={isMobile}
            sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}
          >
            Refresh
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Staff</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Role</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Check In</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Check Out</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', md: 'table-cell' } }}>OT</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}><Skeleton variant="text" width="100%" height={40} /></TableCell>
                  </TableRow>
                ))
              ) : (
                staff.map((member) => {
                  const todayAtt = todayAttendance.find((a) => a.staff_id === member.id);
                  return (
                    <TableRow key={member.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: getRoleInfo(member.role).color, width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {getRoleInfo(member.role).icon}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {member.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                        {getRoleInfo(member.role).label}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{todayAtt?.check_in || '-'}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                        {todayAtt?.check_out || '-'}
                      </TableCell>
                      <TableCell>
                        {todayAtt ? (
                          <Chip 
                            size="small" 
                            label={todayAtt.status} 
                            color={todayAtt.status === 'present' ? 'success' : todayAtt.status === 'late' ? 'warning' : 'error'} 
                            sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 20 }} 
                          />
                        ) : (
                          <Chip 
                            size="small" 
                            label="Not Marked" 
                            variant="outlined" 
                            sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 20 }} 
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                        {todayAtt?.overtime || 0}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          variant="text" 
                          onClick={() => openAttendanceDialog(member)}
                          sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                        >
                          Mark
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ==================== RENDER SALARY ====================
  const renderSalary = () => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    return (
      <Box>
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: 1.5 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
            Salary Management / تنخواہ - {MONTHS[currentMonth - 1]} {currentYear}
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            fullWidth={isMobile}
            sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}
          >
            Print All
          </Button>
        </Box>

        <Grid container spacing={2}>
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
              </Grid>
            ))
          ) : (
            staff.map((member) => {
              const salary = getStaffSalary(member.id, currentMonth, currentYear);
              const monthAtt = getStaffAttendance(member.id, currentMonth, currentYear);
              const presentDays = monthAtt.filter((a) => a.status === 'present').length;
              const totalDays = new Date(currentYear, currentMonth, 0).getDate();

              return (
                <Grid item xs={12} md={6} key={member.id}>
                  <Fade in timeout={300}>
                    <Card sx={{ borderRadius: 3 }}>
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                            <Avatar sx={{ bgcolor: getRoleInfo(member.role).color, width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 } }}>
                              {getRoleInfo(member.role).icon}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                                {member.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                                {getRoleInfo(member.role).label}
                              </Typography>
                            </Box>
                          </Box>
                          {salary ? (
                            <Chip icon={<CheckIcon />} label="Paid" color="success" size="small" sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' } }} />
                          ) : (
                            <Chip label="Pending" color="warning" size="small" variant="outlined" sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' } }} />
                          )}
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Attendance</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                              {presentDays}/{totalDays} days
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={(presentDays / totalDays) * 100} 
                            sx={{ height: 6, borderRadius: 3 }} 
                          />
                        </Box>

                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>Base Salary</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                              {formatCurrency(member.base_salary)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>Net Payable</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                              {formatCurrency(salary?.net_salary || member.base_salary)}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Button 
                          variant="outlined" 
                          fullWidth 
                          startIcon={<MoneyIcon />} 
                          onClick={() => openSalaryDialog(member)} 
                          size="small"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                        >
                          {salary ? 'View Slip' : 'Process Salary'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              );
            })
          )}
        </Grid>
      </Box>
    );
  };

  // ==================== RENDER LOANS ====================
  const renderLoans = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
        Loans & Advances / قرضے اور ایڈوانس
      </Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#1a1a2e' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Staff</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Type</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Amount</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Remaining</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' }, display: { xs: 'none', md: 'table-cell' } }}>Monthly</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}><Skeleton variant="text" width="100%" height={40} /></TableCell>
                </TableRow>
              ))
            ) : loans.length > 0 ? (
              loans.map((loan) => {
                const member = staff.find((s) => s.id === loan.staff_id);
                return (
                  <TableRow key={loan.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: getRoleInfo(member?.role).color, width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                          {getRoleInfo(member?.role).icon}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                          {member?.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={loan.type === 'advance' ? 'Advance' : 'Loan'} 
                        color={loan.type === 'advance' ? 'info' : 'warning'} 
                        sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, height: 20 }} 
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#d32f2f', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                      {formatCurrency(loan.amount)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                      {formatCurrency(loan.remaining)}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                      {formatCurrency(loan.monthly_deduction)}/month
                    </TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={loan.status} 
                        color={loan.status === 'active' ? 'warning' : 'success'} 
                        sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, height: 20 }} 
                      />
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="text" 
                        onClick={() => { setSelectedStaff(member); setLoanDialog(true); }}
                        sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                      >
                        Add Payment
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <AtmIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1, opacity: 0.3 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>No loans recorded</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ==================== RENDER PERFORMANCE ====================
  const renderPerformance = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
        Staff Performance / کارکردگی
      </Typography>
      <Grid container spacing={2}>
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 3 }} />
            </Grid>
          ))
        ) : (
          staff.map((member) => {
            const roleInfo = getRoleInfo(member.role);
            const rating = Math.min(5, Math.max(1, Math.random() * 4 + 1));
            const totalSales = Math.floor(Math.random() * 100000);
            const ordersServed = Math.floor(Math.random() * 50);

            return (
              <Grid item xs={12} sm={6} md={4} key={member.id}>
                <Fade in timeout={300}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: roleInfo.color, width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                          {roleInfo.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                            {member.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: roleInfo.color, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                            {roleInfo.label}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Performance Rating</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {rating.toFixed(1)}/5.0
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(rating / 5) * 100} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4, 
                            bgcolor: '#e0e0e0', 
                            '& .MuiLinearProgress-bar': { 
                              bgcolor: rating >= 4 ? '#4caf50' : rating >= 3 ? '#ff9800' : '#f44336' 
                            } 
                          }} 
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Total Sales</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                          {formatCurrency(totalSales)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Orders Served</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                          {ordersServed}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Avg Order Value</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                          {formatCurrency(ordersServed ? totalSales / ordersServed : 0)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })
        )}
      </Grid>
    </Box>
  );

  // ==================== MAIN RETURN ====================
  return (
    <Box sx={{ 
      px: { xs: 1, sm: 2, md: 3 },
      pb: { xs: 8, sm: 3 },
    }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ 
        mb: { xs: 2, sm: 3 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        flexWrap: 'wrap', 
        gap: 2 
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: '#1a1a2e', 
            mb: 0.5,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
          }}>
            Staff Management
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            سٹاف مینجمنٹ | Employees, Attendance, Salary & Loans
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            sx={{ borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Export'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => openStaffDialog()} 
            fullWidth={isMobile}
            sx={{ 
              bgcolor: '#ff6b35', 
              '&:hover': { bgcolor: '#e55a2b' }, 
              borderRadius: 2,
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              px: { xs: 2, sm: 3 }
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Add' : 'Add Staff'}
          </Button>
        </Box>
      </Box>

      {/* Stats Summary */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <StatCard 
          label="Total Staff" 
          value={staff.length} 
          color="#1976d2" 
          icon={<PersonIcon />} 
        />
        <StatCard 
          label="Active Today" 
          value={attendance.filter((a) => a.status === 'present' && a.date === new Date().toISOString().split('T')[0]).length} 
          color="#4caf50" 
          icon={<CheckIcon />} 
        />
        <StatCard 
          label="Pending Salaries" 
          value={staff.length - salaryRecords.filter((s) => s.month === new Date().getMonth() + 1).length} 
          color="#ff9800" 
          icon={<MoneyIcon />} 
        />
        <StatCard 
          label="Active Loans" 
          value={loans.filter((l) => l.status === 'active').length} 
          color="#d32f2f" 
          icon={<WarningIcon />} 
        />
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, mb: 2, overflow: 'auto' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)} 
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}
        >
          <Tab 
            icon={<PersonIcon />} 
            label={isMobile ? '' : 'Staff List'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
          />
          <Tab 
            icon={<TimeIcon />} 
            label={isMobile ? '' : 'Attendance'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
          />
          <Tab 
            icon={<MoneyIcon />} 
            label={isMobile ? '' : 'Salary'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
          />
          <Tab 
            icon={<AtmIcon />} 
            label={isMobile ? '' : 'Loans'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
          />
          <Tab 
            icon={<TrendingIcon />} 
            label={isMobile ? '' : 'Performance'} 
            sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
          />
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, height: 4 }} />}

      <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {activeTab === 0 && renderStaffList()}
        {activeTab === 1 && renderAttendance()}
        {activeTab === 2 && renderSalary()}
        {activeTab === 3 && renderLoans()}
        {activeTab === 4 && renderPerformance()}
      </Box>

      {/* ===== DIALOGS ===== */}
      {/* Staff Dialog */}
      <Dialog open={staffDialog} onClose={() => setStaffDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#1a1a2e',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}>
          {selectedStaff ? 'Edit Staff' : 'Add Staff'} 
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
            {selectedStaff ? 'ترمیم' : 'نیا سٹاف'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Full Name" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Role / عہدہ</InputLabel>
                <Select value={staffForm.role} label="Role" onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}>
                  {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.icon} {r.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone" value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Email" type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Base Salary" type="number" value={staffForm.base_salary} onChange={(e) => setStaffForm({ ...staffForm, base_salary: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Joining Date" type="date" value={staffForm.joining_date} onChange={(e) => setStaffForm({ ...staffForm, joining_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={staffForm.is_active} onChange={(e) => setStaffForm({ ...staffForm, is_active: e.target.checked })} />} label="Active Employee" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setStaffDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={saveStaff} variant="contained" startIcon={<SaveIcon />} disabled={!staffForm.name.trim() || !staffForm.phone} sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={attendanceDialog} onClose={() => setAttendanceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Mark Attendance - {selectedStaff?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="date" label="Date" value={attendanceForm.date} onChange={(e) => setAttendanceForm({ ...attendanceForm, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={attendanceForm.status} label="Status" onChange={(e) => setAttendanceForm({ ...attendanceForm, status: e.target.value })}>
                  <MenuItem value="present">Present / حاضر</MenuItem>
                  <MenuItem value="absent">Absent / غیر حاضر</MenuItem>
                  <MenuItem value="late">Late / دیر سے</MenuItem>
                  <MenuItem value="half_day">Half Day / آدھا دن</MenuItem>
                  <MenuItem value="leave">Leave / رخصت</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="time" label="Check In" value={attendanceForm.check_in} onChange={(e) => setAttendanceForm({ ...attendanceForm, check_in: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="time" label="Check Out" value={attendanceForm.check_out} onChange={(e) => setAttendanceForm({ ...attendanceForm, check_out: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAttendanceDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={saveAttendance} variant="contained" startIcon={<SaveIcon />} sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Salary Dialog */}
      <Dialog open={salaryDialog} onClose={() => setSalaryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Process Salary - {selectedStaff?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select value={salaryForm.month} label="Month" onChange={(e) => setSalaryForm({ ...salaryForm, month: e.target.value })}>
                  {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Year" type="number" value={salaryForm.year} onChange={(e) => setSalaryForm({ ...salaryForm, year: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Base Salary" type="number" value={salaryForm.base_salary} onChange={(e) => setSalaryForm({ ...salaryForm, base_salary: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Bonus" type="number" value={salaryForm.bonus} onChange={(e) => setSalaryForm({ ...salaryForm, bonus: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Deductions" type="number" value={salaryForm.deductions} onChange={(e) => setSalaryForm({ ...salaryForm, deductions: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Net Salary: {formatCurrency(
                    parseFloat(salaryForm.base_salary || 0) +
                    parseFloat(salaryForm.bonus || 0) +
                    parseFloat(salaryForm.overtime_pay || 0) -
                    parseFloat(salaryForm.deductions || 0) -
                    parseFloat(salaryForm.advance_deducted || 0) -
                    parseFloat(salaryForm.loan_deducted || 0)
                  )}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setSalaryDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={saveSalary} variant="contained" startIcon={<SaveIcon />} sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}>
            Process
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loan Dialog */}
      <Dialog open={loanDialog} onClose={() => setLoanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          {selectedStaff?.name} - Loan/Advance
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={loanForm.type} label="Type" onChange={(e) => setLoanForm({ ...loanForm, type: e.target.value })}>
                  <MenuItem value="advance">Salary Advance / ایڈوانس</MenuItem>
                  <MenuItem value="loan">Personal Loan / قرضہ</MenuItem>
                  <MenuItem value="emergency">Emergency / ایمرجنسی</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Amount" type="number" value={loanForm.amount} onChange={(e) => setLoanForm({ ...loanForm, amount: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="date" label="Date" value={loanForm.date} onChange={(e) => setLoanForm({ ...loanForm, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Installments" type="number" value={loanForm.installments} onChange={(e) => setLoanForm({ ...loanForm, installments: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason / وجہ" multiline rows={2} value={loanForm.reason} onChange={(e) => setLoanForm({ ...loanForm, reason: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setLoanDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={saveLoan} variant="contained" startIcon={<SaveIcon />} disabled={!loanForm.amount} sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#1a1a2e',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          Staff Profile - {detailStaff?.name}
          <IconButton onClick={() => setDetailDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailStaff && (
            <Grid container spacing={3} sx={{ pt: 1 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: '#f8f9fa' }}>
                  <Avatar sx={{ bgcolor: getRoleInfo(detailStaff.role).color, width: 80, height: 80, fontSize: '2.5rem', mx: 'auto', mb: 2 }}>
                    {getRoleInfo(detailStaff.role).icon}
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                    {detailStaff.name}
                  </Typography>
                  <Chip label={getRoleInfo(detailStaff.role).label} sx={{ mt: 1, bgcolor: getRoleInfo(detailStaff.role).color, color: '#fff' }} />
                  <Box sx={{ mt: 2, textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      <strong>Phone:</strong> {detailStaff.phone}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      <strong>Email:</strong> {detailStaff.email || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      <strong>Joined:</strong> {detailStaff.joining_date}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      <strong>Status:</strong> {detailStaff.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                  Salary History
                </Typography>
                <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                        <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Month</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Base</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Bonus</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Deductions</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Net</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salaryRecords.filter((s) => s.staff_id === detailStaff.id).map((s) => (
                        <TableRow key={s.id}>
                          <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{MONTHS[s.month - 1]} {s.year}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatCurrency(s.base_salary)}</TableCell>
                          <TableCell sx={{ color: '#4caf50', fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>+{formatCurrency(s.bonus)}</TableCell>
                          <TableCell sx={{ color: '#f44336', fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>-{formatCurrency(s.deductions + s.advance_deducted + s.loan_deducted)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#1976d2', fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{formatCurrency(s.net_salary)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                  Active Loans
                </Typography>
                {loans.filter((l) => l.staff_id === detailStaff.id && l.status === 'active').length > 0 ? (
                  loans.filter((l) => l.staff_id === detailStaff.id && l.status === 'active').map((l) => (
                    <Paper key={l.id} sx={{ p: 2, mb: 1, borderRadius: 2, borderLeft: '4px solid #ff9800' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                          {l.type.toUpperCase()}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                          {formatCurrency(l.remaining)} remaining
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                        {formatCurrency(l.monthly_deduction)}/month • {l.reason}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    No active loans
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffManagement;